import { BaseService } from '@/services/base';
import { httpsCallable, Functions } from 'firebase/functions';
import {
  Question,
  AdaptiveState,
  SimpleAdaptiveState,
  TestType,
  AdaptiveMetrics,
  StudyPlan,
  SessionProgress,
  QuizSummary,
  DetailedFeedback,
  NextRecommendations,
  TestResult,
  SessionConfig,
  SessionBlockIdentity,
  QuestionAudioUnit,
  convertBackendQuestionToFrontend
} from '@/types/adaptive';
import type { CheckoutSessionResponse } from '@/types/checkout';
import { StorageAdaptiveState, createDefaultAdaptiveMetrics, AdaptiveMetrics as ModelAdaptiveMetrics } from '@/models/AdaptiveState';
import { logger } from '@/utils/logger';
import { getCheckoutAttribution } from '@/utils/emailAttribution';
import app from '@/firebase';
import { createFunctions, ensureFunctionsEmulator } from '@/utils/firebase/emulator';
import { WrongAnswer as UserWrongAnswer, TestResult as UserTestResult } from '@/models/UserTypes';
import { QuizType } from '@/models/SharedTypes';
import { callWithTimeout, CallTimeoutError } from '@/utils/callWithTimeout';
import { trackLatency } from '@/utils/trackLatency';
import { DailyFreeAllowance, DailyLimitError, parseDailyLimitError } from '@/utils/freeTier';
import { TimeExpiredError, isRawTimeExpiredError } from '@/utils/timeLimit';

// Lazy initialization of Firebase Functions
let functions: Functions | null = null;
const getFunctionsInstance = () => {
  if (!functions) {
    // createFunctions applies VITE_FUNCTIONS_BASE_URL (custom domain) when set,
    // otherwise the SDK default region (us-central1).
    functions = createFunctions(app);
  }
  return functions;
};

const connectToEmulatorIfNeeded = () => {
  try {
    ensureFunctionsEmulator(getFunctionsInstance());
  } catch (error) {
    logger.error('QuizApiService', 'Failed to connect to emulators', { error });
    throw new Error('Failed to connect to emulators. Make sure they are running in the backend repo.');
  }
};

// Backend request/response interfaces
interface InitializeSessionRequest {
  testType: string;
  preferredDifficulty?: number;
  /** Backend-owned session shape: practice/category = endless, mock = blueprint length. */
  sessionMode?: 'practice' | 'category' | 'mock';
  targetQuestionCount?: number;
  categories?: string[];
  /** Deep-link: start the session on this exact question id (e.g. an email "try this question" CTA) */
  startQuestionId?: string;
  /** Strip correct_answer/explanation/hint from outbound question objects */
  omitAnswerKey?: boolean;
}

interface LearningPreferences {
  studyTime: 'morning' | 'afternoon' | 'evening';
  sessionLength: 'short' | 'medium' | 'long';
  difficulty: 'adaptive' | 'fixed';
  categories: string[];
}

interface InitializeSessionResponse extends SessionBlockIdentity {
  sessionId: string;
  firstQuestion: Question;
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  sessionConfig: {
    /** null = endless practice (no fixed length; render "N answered") */
    targetQuestions: number | null;
    estimatedDuration: number | null;
    focusAreas: string[];
  };
  userProfile: {
    skillLevel: number;
    categoryStrengths: Record<string, number>;
    learningPreferences: LearningPreferences;
  };
  /** Metered free-tier remaining allowance (present only for capped users). */
  dailyFree?: DailyFreeAllowance;
}

interface EnhancedAnswerRequest {
  sessionId: string;
  questionId: string;
  selectedAnswer: string;
  timeSpent: number;
  testType: string;
  metadata?: {
    hintsUsed?: number;
    confidence?: number;
    timeSpentReading?: number;      // NEW: Time spent reading before answering (seconds)
    flaggedForReview?: boolean;     // NEW: User marked question for later review
  };
  questionFeedback?: {
    feedback: 'positive' | 'negative';
    timestamp: Date;
  };
  /** Strip correct_answer/explanation/hint from the outbound nextQuestion
   * (the response's isCorrect/correctAnswer/explanation reveal is unaffected) */
  omitAnswerKey?: boolean;
}

interface EnhancedAnswerResponse {
  isCorrect: boolean;
  correctAnswer: string;
  explanation: string;
  detailedFeedback: DetailedFeedback;
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  adaptiveChanges: {
    difficultyAdjustment: number;
    categoryStrengthChanges: Record<string, number>;
    newInsights: string[];
  };
  nextQuestion: Question | null;
  nextQuestionNote?: string; // New field for next question repeat notes
  sessionProgress: SessionProgress;
  sessionComplete: boolean;
  finalSummary?: QuizSummary;
  nextRecommendations?: NextRecommendations;
  /** Metered free-tier remaining allowance (present only for capped users). */
  dailyFree?: DailyFreeAllowance;
  /**
   * True when this answer was recorded but the follow-up question was withheld
   * because the free allowance is spent. The call SUCCEEDS (the answer counts),
   * so this flag is the only signal that `nextQuestion: null` means "capped"
   * rather than "the adaptive engine is still choosing".
   */
  dailyLimitReached?: boolean;
  /** Pam's spoken reveal — the narrated twin of correctAnswer/explanation.
   *  Only exists on THIS response (the sanitizer strips both from question
   *  payloads pre-submission, same rule as the text answer key). */
  audioAnswer?: QuestionAudioUnit | null;
  audioExplanation?: QuestionAudioUnit | null;
}

/**
 * Contract shape (aswb-backend `PauseSessionStateHintSchema`, openapi.yaml).
 * The handler reads `currentState.currentQuestionIndex`, `.score` and
 * `.adaptiveState` — nothing else. The old web payload
 * ({ currentQuestion, progress }) shared ZERO keys with it and was silently
 * accepted by the schema's .passthrough(), so every pause persisted whatever
 * the backend last stored on answerQuestion instead of the pause moment (#330).
 *
 * `adaptiveState` is deliberately OMITTED: the client's copy is only ever an
 * echo of the last answerQuestion response, so re-sending it can only rewrite
 * the backend's own (never staler) copy. The handler falls back to its stored
 * value when the key is absent.
 */
interface PauseSessionRequest {
  sessionId: string;
  currentState?: {
    /** Questions COMPLETED at pause time — the handler stores it as
     *  `currentQuestionIndex` and reports it as `questionsCompleted`. */
    currentQuestionIndex?: number;
    /** RAW correct count, never a percentage. */
    score?: number;
  };
  pauseReason?: string;
}

interface PauseSessionResponse {
  pausedAt: string;
  resumeToken: string;
  sessionSummary: {
    questionsCompleted: number;
    timeSpent: number;
    currentScore: number;
  };
  canResume: boolean;
  expiresAt: string;
}

interface ResumeSessionRequest {
  sessionId: string;
  resumeToken: string;
  /** Strip correct_answer/explanation/hint from outbound question objects */
  omitAnswerKey?: boolean;
}

interface ResumeSessionResponse extends SessionBlockIdentity {
  sessionRestored: boolean;
  currentQuestion: Question;
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  sessionProgress: SessionProgress;
  testType?: string;
  currentQuestionNote?: string | null;
  sessionConfig?: SessionConfig;
  sessionAskedQuestions?: string[];
  /** Metered free-tier remaining allowance (present only for capped users). */
  dailyFree?: DailyFreeAllowance;
}

interface GetNextQuestionRequest {
  testType?: TestType;
  sessionId?: string;
  forceCategory?: string;
  forceDifficulty?: number;
  /** Strip correct_answer/explanation/hint from outbound question objects */
  omitAnswerKey?: boolean;
}

interface GetNextQuestionResponse {
  question: Question | null;
  adaptiveState: SimpleAdaptiveState | AdaptiveState | null;
  message: string;
  note?: string; // New field for repeat question notes
  sessionContext?: {
    sessionId: string;
    enhanced: boolean;
  };
}

interface LoadSessionRequest {
  sessionId: string;
  /** Strip correct_answer/explanation/hint from outbound question objects */
  omitAnswerKey?: boolean;
}

interface LoadSessionResponse extends SessionBlockIdentity {
  sessionId: string;
  testType: string;
  status: string;
  currentQuestion: Question | null;
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  sessionProgress: SessionProgress;
  sessionConfig: {
    targetQuestions: number | null;
    estimatedDuration: number | null;
    focusAreas: string[];
  };
  currentQuestionNote?: string | null;
  nextQuestion?: Question | null;
  nextQuestionNote?: string | null;
  sessionAskedQuestions?: string[];
  sessionComplete?: boolean;
  finalSummary?: QuizSummary;
  waitingForBackend?: boolean;
  /** Metered free-tier remaining allowance (present only for capped users). */
  dailyFree?: DailyFreeAllowance;
}

interface DashboardStats {
  totalQuestionsAttempted: number;
  correctAnswers: number;
  averageScore: number;
  studyStreak: number;
  masteryLevel: number;
  completedTests: number;
  totalStudyHours: number;
}

interface QuizResultsResponse extends SessionBlockIdentity {
  sessionId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeSpent: number;
  wrongAnswers: UserWrongAnswer[]; // Backend format
  quizProgress: QuizSummary;
  finalSummary?: QuizSummary; // ✅ Added: Proper finalSummary from backend
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  currentQuestionIndex: number;
  targetQuestions: number | null;
  testType?: string;
  sessionConfig?: SessionConfig;
  sessionAskedQuestions?: string[];
}

const mapQuizTypeFromMode = (mode?: string): QuizType => {
  switch (mode) {
    case 'full':
      return QuizType.FULL;
    case 'category':
      return QuizType.CATEGORY;
    default:
      return QuizType.SAMPLE;
  }
};

const convertWrongAnswersToUserFormat = (result: TestResult): UserWrongAnswer[] => {
  if (!result.wrongAnswers) {
    return [];
  }

  const questionsById = new Map((result.questions ?? []).map(question => [question.id, question]));

  return result.wrongAnswers.map(wrongAnswer => {
    if ('question' in wrongAnswer) {
      return {
        question: {
          id: wrongAnswer.question.id,
          category: wrongAnswer.question.category,
          question: wrongAnswer.question.question,
          correct_answer: wrongAnswer.question.correct_answer ?? '',
          selected_answer: wrongAnswer.question.selected_answer ?? undefined
        },
        explanation: wrongAnswer.explanation ?? '',
        date: new Date((wrongAnswer.timestamp ?? Date.now())).toISOString(),
        timestamp: wrongAnswer.timestamp ?? Date.now(),
        isReviewed: false,
        attempts: 1
      } satisfies UserWrongAnswer;
    }

    const relatedQuestion = questionsById.get(wrongAnswer.questionId);

    return {
      question: {
        id: wrongAnswer.questionId,
        category: wrongAnswer.category,
        question: relatedQuestion?.question ?? '',
        correct_answer: wrongAnswer.correctAnswer,
        selected_answer: wrongAnswer.selectedAnswer
      },
      explanation: wrongAnswer.explanation ?? '',
      date: new Date().toISOString(),
      timestamp: Date.now(),
      isReviewed: false,
      attempts: 1
    } satisfies UserWrongAnswer;
  });
};

const convertTestResultToUserFormat = (result: TestResult): UserTestResult => {
  // No backend metrics means "not computed", not "75 seconds"; the default's
  // averageResponseTime is an engine prior and must not render as a learner's result.
  const adaptiveMetrics: ModelAdaptiveMetrics = result.adaptiveMetrics
    ? { ...result.adaptiveMetrics }
    : { ...createDefaultAdaptiveMetrics(), averageResponseTime: 0 };

  return {
    id: result.id,
    date: result.date,
    score: result.score ?? 0,
    totalQuestions: result.totalQuestions ?? result.questions?.length ?? 0,
    percentage: result.percentage ?? 0,
    questions: result.questions ?? [],
    questionIds: result.questionIds ?? [],
    wrongAnswers: convertWrongAnswersToUserFormat(result),
    type: mapQuizTypeFromMode(result.quizMode),
    category: result.category,
    timeTaken: result.timeTaken,
    adaptiveMetrics,
    adaptiveState: undefined
  };
};

interface QuizSession {
  sessionId: string;
  testType: string;
  status: string;
  currentQuestionIndex: number;
  totalQuestions: number | null;
  score: number;
  lastActivityAt: string;
  createdAt: string;
}


interface CheckoutSessionRequest {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  clientReferenceId?: string;
  /** Marketing attribution (utm_*) — injected automatically in createCheckoutSession. */
  attribution?: Record<string, string>;
}

interface PortalSessionRequest {
  returnUrl?: string;
  customerId?: string;
}

interface PortalSessionResponse {
  url: string;
}

/**
 * Unified API service for all quiz and adaptive learning backend interactions
 * Consolidates QuizService and AdaptiveApiService into a single, clean interface
 */
export class QuizApiService extends BaseService {
  private static instance: QuizApiService;

  private quizFunctionsWarmed = false;

  /**
   * Fire-and-forget pre-warm pings to the quiz callables so the user's
   * first real quiz interaction doesn't pay a 2-5s cold start. The
   * backend short-circuits on { warmup: true }; invoking the function
   * at all is what loads its module (the expensive part). Called from
   * the dashboard on mount; deduped to once per page load.
   */
  warmQuizFunctions(): void {
    if (this.quizFunctionsWarmed) return;
    this.quizFunctionsWarmed = true;
    connectToEmulatorIfNeeded();
    const functions = getFunctionsInstance();
    // Quiz callables only. getUserProfile is intentionally NOT warmed here: the
    // dashboard's real getUserProfile fires on the same auth tick (useUserDataState)
    // as this warm-up, so they race — warming it just adds an invocation without
    // beating its own cold start. The quiz callables DO benefit: there's a gap
    // between landing on the dashboard and starting a quiz.
    for (const name of ['initializeQuizSession', 'getNextQuestion', 'answerQuestion']) {
      void httpsCallable(functions, name)({ warmup: true }).catch(() => {
        // Warm-up is best-effort; failures are irrelevant to the user
      });
    }
  }

  /**
   * 5-minute TTL cache for the dashboard's two read-heavy callables
   * (getAdaptiveMetrics + getStudyPlan). Memory layer covers route
   * re-entries; sessionStorage layer covers full page reloads. Keys
   * include the uid so a login switch in the same tab never serves
   * another user's data. Invalidated on quiz completion and via
   * invalidateDashboardCache() (refreshBackendData).
   */
  private dashboardCache = new Map<string, { value: unknown; fetchedAt: number }>();
  private static readonly DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;
  private static readonly DASHBOARD_CACHE_PREFIX = 'swtpa.dash.';

  private dashboardCacheGet<T>(key: string): T | null {
    const ttl = QuizApiService.DASHBOARD_CACHE_TTL_MS;
    const mem = this.dashboardCache.get(key);
    if (mem && Date.now() - mem.fetchedAt < ttl) {
      return mem.value as T;
    }
    try {
      const raw = sessionStorage.getItem(QuizApiService.DASHBOARD_CACHE_PREFIX + key);
      if (raw) {
        const parsed = JSON.parse(raw) as { value: T; fetchedAt: number };
        if (Date.now() - parsed.fetchedAt < ttl) {
          this.dashboardCache.set(key, parsed);
          return parsed.value;
        }
      }
    } catch {
      // sessionStorage unavailable or corrupt entry — treat as miss
    }
    return null;
  }

  private dashboardCacheSet(key: string, value: unknown): void {
    const entry = { value, fetchedAt: Date.now() };
    this.dashboardCache.set(key, entry);
    try {
      sessionStorage.setItem(QuizApiService.DASHBOARD_CACHE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // Quota/unavailable — memory layer still works
    }
  }

  invalidateDashboardCache(): void {
    this.dashboardCache.clear();
    try {
      // Standard Storage iteration (Object.keys is not reliable on all
      // Storage implementations); iterate backwards while removing
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(QuizApiService.DASHBOARD_CACHE_PREFIX)) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {
      // ignore
    }
  }

  /**
   * Every answered question changes TODAY's study activity (heat map + daily
   * goal), so drop just that cache entry after each answer. The metrics/plan
   * entries stay cached — they still refresh on session completion via
   * invalidateDashboardCache(). Without this, a user who paused/abandoned
   * mid-session came back to a "Today's goal 0/N" served from the pre-quiz
   * snapshot for up to the cache TTL.
   */
  private invalidateStudyActivityCache(): void {
    for (const key of [...this.dashboardCache.keys()]) {
      if (key.startsWith('studyActivity.')) this.dashboardCache.delete(key);
    }
    try {
      const prefix = QuizApiService.DASHBOARD_CACHE_PREFIX + 'studyActivity.';
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(prefix)) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {
      // sessionStorage unavailable — memory layer already cleared
    }
  }

  private async currentUid(): Promise<string> {
    const { auth } = await import('../firebase');
    return auth.currentUser?.uid ?? 'anon';
  }

  // Lazy getters for backend function references
  private get initializeQuizSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<InitializeSessionRequest, InitializeSessionResponse>(getFunctionsInstance(), 'initializeQuizSession');
  }
  
  private get answerQuestionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<EnhancedAnswerRequest, EnhancedAnswerResponse>(getFunctionsInstance(), 'answerQuestion');
  }
  
  private get getNextQuestionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<GetNextQuestionRequest, GetNextQuestionResponse>(getFunctionsInstance(), 'getNextQuestion');
  }
  
  private get pauseQuizSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<PauseSessionRequest, PauseSessionResponse>(getFunctionsInstance(), 'pauseQuizSession');
  }
  
  private get resumeQuizSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<ResumeSessionRequest, ResumeSessionResponse>(getFunctionsInstance(), 'resumeQuizSession');
  }
  
  private get loadQuizSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<LoadSessionRequest, LoadSessionResponse>(getFunctionsInstance(), 'loadQuizSession');
  }
  
  private get getAdaptiveMetricsFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<{ includeDetailedBreakdown: boolean }, { metrics: AdaptiveMetrics; dashboardStats: DashboardStats; message: string }>(getFunctionsInstance(), 'getAdaptiveMetrics');
  }
  
  private get getStudyPlanFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<Record<string, never>, { studyPlan: StudyPlan | null; message: string }>(getFunctionsInstance(), 'getStudyPlan');
  }

  private get getStudyActivityFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<Record<string, never>, StudyActivityResult>(getFunctionsInstance(), 'getStudyActivity');
  }
  
  private get getQuizResultsFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<{ sessionId: string }, QuizResultsResponse>(getFunctionsInstance(), 'getQuizResults');
  }
  
  private get saveTestResultFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<{ testResult: TestResult; adaptiveState: SimpleAdaptiveState | AdaptiveState | StorageAdaptiveState }, { success: boolean; testResultId: string; message: string }>(getFunctionsInstance(), 'saveTestResult');
  }
  
  private get getUserTestResultsFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<{ limit?: number; offset?: number }, { success: boolean; testResults: TestResult[]; summary: TestResultsSummary }>(getFunctionsInstance(), 'getUserTestResults');
  }
  
  // Quiz management functions
  private get getUserActiveSessionsFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<void, { sessions: QuizSession[] }>(getFunctionsInstance(), 'getUserActiveSessions');
  }
  
  private get deleteQuizSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<{ sessionId: string }, { success: boolean }>(getFunctionsInstance(), 'deleteQuizSession');
  }
  
  // Stripe functions
  private get createCheckoutSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<CheckoutSessionRequest, CheckoutSessionResponse>(getFunctionsInstance(), 'createCheckoutSession');
  }

  private get createStripePortalSessionFn() {
    connectToEmulatorIfNeeded();
    return httpsCallable<PortalSessionRequest, PortalSessionResponse>(getFunctionsInstance(), 'createStripePortalSession');
  }

  private constructor() {
    super();
  }

  static getInstance(): QuizApiService {
    if (!QuizApiService.instance) {
      QuizApiService.instance = new QuizApiService();
    }
    return QuizApiService.instance;
  }

  /**
   * Enhanced error handling with backend-specific error codes
   */
  private handleBackendError(error: unknown, context: string): never {
    const errorCode = (error as { code?: string })?.code;
    const errorMessage = (error as { message?: string })?.message || 'Unknown error';
    
    // A timed trial block whose wall clock ran out arrives as
    // `failed-precondition: day4_time_expired`. Without this it fell into the
    // generic failed-precondition arm below and told the learner their account
    // was not set up — every caller routes through here, so classify once.
    if (isRawTimeExpiredError(error)) {
      logger.info('QuizApiService', `${context} hit the timed block's wall clock`, { context });
      throw new TimeExpiredError();
    }

    // Metered free tier: a hit daily cap arrives as `resource-exhausted` with
    // details { remaining, limit, resetsAtUtc }. Detect it BEFORE the
    // error-level log below — hitting the cap is an expected, potentially
    // frequent user-flow event, not a failure, so it logs at info. Re-thrown
    // as a typed DailyLimitError so the quiz UI can render the in-flow paywall
    // instead of the generic "unable to load" card. Inert when the backend
    // never emits it (i.e. before the free-tier cap ships), so this is safe
    // with the flag OFF.
    const dailyLimit = parseDailyLimitError(error);
    if (dailyLimit) {
      logger.info('QuizApiService', `${context} hit the daily free-question cap`, {
        limit: dailyLimit.limit,
        resetsAtUtc: dailyLimit.resetsAtUtc,
        context
      });
      throw new DailyLimitError(dailyLimit);
    }

    logger.error('QuizApiService', `${context} failed`, {
      error: errorMessage,
      code: errorCode,
      context
    });

    // Handle specific backend error codes from our recent backend improvements
    switch (errorCode) {
      case 'functions/failed-precondition':
        // New: Handle "no questions available" gracefully
        if (errorMessage.includes('No questions available')) {
          throw new Error('Test content is being updated. Please try again in a few minutes.');
        }
        throw new Error('Unable to start quiz. Please ensure your account is properly set up.');
        
      case 'functions/unauthenticated':
        throw new Error('Please sign in again to continue.');
        
      case 'functions/permission-denied':
        throw new Error('You don\'t have permission to access this content. Please check your subscription.');
        
      case 'functions/not-found':
        if (context.includes('session')) {
          throw new Error('Quiz session not found. It may have expired or been deleted.');
        }
        throw new Error('The requested content could not be found.');
        
      case 'functions/unavailable':
        throw new Error('Service temporarily unavailable. Please try again in a moment.');
        
      case 'functions/deadline-exceeded':
        throw new Error('Request timed out. Please check your connection and try again.');
        
      case 'functions/internal':
        throw new Error('An internal error occurred. Our team has been notified.');
        
      default:
        // For unknown errors, provide generic but helpful message
        if (errorMessage.includes('CORS') || errorMessage.includes('cors')) {
          throw new Error('Connection issue detected. Attempting to resolve...');
        }
        
        throw new Error(errorMessage || 'An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Initialize a new quiz session
   */
  async initializeSession(request: InitializeSessionRequest): Promise<InitializeSessionResponse> {
    try {
      logger.debug('QuizApiService', 'Initializing quiz session', request);

      // Mutation (creates a session): retries:0 to avoid duplicate sessions.
      const result = await trackLatency('initializeSession', () => callWithTimeout(
        () => this.initializeQuizSessionFn({ ...request, omitAnswerKey: true }),
        { retries: 0 }
      ));
      const response = result.data;

      logger.debug('QuizApiService', 'Session initialized successfully', {
        sessionId: response.sessionId,
        targetQuestions: response.sessionConfig.targetQuestions
      });

      return response;
    } catch (error) {
      this.handleBackendError(error, 'Session initialization');
    }
  }

  /**
   * Submit answer with enhanced backend processing
   */
  async submitAnswer(request: EnhancedAnswerRequest): Promise<EnhancedAnswerResponse> {
    try {
      logger.debug('QuizApiService', 'Submitting answer to backend', {
        sessionId: request.sessionId,
        questionId: request.questionId,
        selectedAnswer: request.selectedAnswer
      });
      
      logger.debug('QuizApiService', 'Submitting answer for sessionId', { sessionId: request.sessionId });

      // Debug authentication state
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      logger.debug('QuizApiService', 'Authentication check', {
        isAuthenticated: !!user,
        userId: user?.uid,
        hasToken: !!await user?.getIdToken().catch(() => null)
      });

      // Mutation: retries:0 — a timed-out answer may have landed server-side,
      // so auto-retry could double-submit.
      const result = await trackLatency('answerQuestion', () => callWithTimeout(
        () => this.answerQuestionFn({ ...request, omitAnswerKey: true }),
        { retries: 0 }
      ));
      const response = result.data;

      logger.debug('QuizApiService', 'Answer submission successful', {
        isCorrect: response.isCorrect,
        sessionComplete: response.sessionComplete,
        hasNextQuestion: !!response.nextQuestion
      });

      // Completed quiz changes metrics/study plan — drop the cached dashboard.
      // Any answered question changes TODAY's study activity (daily goal +
      // heat map), so that entry is dropped even mid-session.
      if (response.sessionComplete) {
        this.invalidateDashboardCache();
      } else {
        this.invalidateStudyActivityCache();
      }

      return response;
    } catch (error) {
      const firebaseError = error as { code?: string; message?: string; details?: unknown };
      logger.error('QuizApiService', 'Full error details', {
        error,
        code: firebaseError?.code,
        message: firebaseError?.message,
        details: firebaseError?.details,
        stack: error instanceof Error ? error.stack : undefined
      });
      this.handleBackendError(error, 'Answer submission');
    }
  }

  /**
   * Get the next question with session support
   */
  async getNextQuestion(request: GetNextQuestionRequest): Promise<GetNextQuestionResponse> {
    try {
      logger.debug('QuizApiService', 'Getting next question', request);

      const result = await trackLatency('getNextQuestion', () => callWithTimeout(
        () => this.getNextQuestionFn({ ...request, omitAnswerKey: true })
      ));
      const response = result.data;

      logger.debug('QuizApiService', 'Got next question', {
        hasQuestion: !!response.question,
        hasSessionContext: !!response.sessionContext
      });

      return response;
    } catch (error) {
      this.handleBackendError(error, 'Get next question');
    }
  }

  /**
   * Pause quiz session
   */
  async pauseSession(request: PauseSessionRequest): Promise<PauseSessionResponse> {
    try {
      logger.debug('QuizApiService', 'Pausing quiz session', { sessionId: request.sessionId });

      // Mutation: retries:0 — a timed-out pause may have landed server-side,
      // and re-pausing is pointless work on an exit the user already made.
      // 10s: pauseQuizSession is a 2-write handler; anything past that is a
      // cold/hung instance and the user is better served by an exit + honest
      // toast than by Firebase's ~70s client default.
      const result = await trackLatency('pauseQuizSession', () => callWithTimeout(
        () => this.pauseQuizSessionFn(request),
        { retries: 0, timeoutMs: 10000 }
      ));
      const response = result.data;

      logger.debug('QuizApiService', 'Session paused successfully', {
        resumeToken: response.resumeToken,
        expiresAt: response.expiresAt
      });

      return response;
    } catch (error) {
      // Surface the timeout AS a timeout — handleBackendError would flatten it
      // into a generic Error and the exit handler could no longer tell a slow
      // save (progress still safe) from a real failure.
      if (error instanceof CallTimeoutError) throw error;
      this.handleBackendError(error, 'Pause session');
    }
  }

  /**
   * Resume quiz session
   */
  async resumeSession(request: ResumeSessionRequest): Promise<ResumeSessionResponse> {
    try {
      logger.debug('QuizApiService', 'Resuming quiz session', { sessionId: request.sessionId });

      // Mutation (flips the session back to active): retries:0, same 10s bound
      // as pause — both callables skipped the wrapper every other quiz call has.
      const result = await trackLatency('resumeQuizSession', () => callWithTimeout(
        () => this.resumeQuizSessionFn({ ...request, omitAnswerKey: true }),
        { retries: 0, timeoutMs: 10000 }
      ));
      const response = result.data;

      logger.debug('QuizApiService', 'Session resumed successfully', {
        sessionRestored: response.sessionRestored
      });

      return response;
    } catch (error) {
      this.handleBackendError(error, 'Resume session');
    }
  }

  /**
   * Load quiz session by ID (the single web restore path)
   */
  async loadSession(request: { sessionId: string }): Promise<LoadSessionResponse> {
    try {
      logger.debug('QuizApiService', 'Loading quiz session', { sessionId: request.sessionId });

      const result = await callWithTimeout(
        () => this.loadQuizSessionFn({ ...request, omitAnswerKey: true })
      );
      const response = result.data;

      logger.debug('QuizApiService', 'Session loaded successfully', {
        sessionId: response.sessionId,
        status: response.status
      });

      return response;
    } catch (error) {
      // METERED FREE TIER — the restore door. loadQuizSession refuses to
      // re-serve a question to a capped free user (backend
      // access/sessionQuestionGuard), throwing the same
      // 'resource-exhausted' / daily-limit-reached error the serving
      // callables throw. This path rethrew it RAW, so the quiz page's
      // `isDailyLimitError` (instanceof) check missed it and a capped user
      // resuming a session got the generic "unable to load" card instead of
      // the paywall. Classify it here, exactly like handleBackendError does
      // for init/submit, so all three doors render one paywall.
      const dailyLimit = parseDailyLimitError(error);
      if (dailyLimit) {
        logger.info('QuizApiService', 'Load session hit the daily free-question cap', {
          limit: dailyLimit.limit,
          resetsAtUtc: dailyLimit.resetsAtUtc,
        });
        throw new DailyLimitError(dailyLimit);
      }

      // NOTE (2026-07-27): the old CORS "fallback" here fetched a Cloud
      // Function that never existed on the backend — a second doomed request
      // and a misleading log path. Deleted in the restore-path unification.
      logger.error('QuizApiService', 'Failed to load session', { error });
      throw error;
    }
  }

  /**
   * Get adaptive learning metrics
   */
  async getAdaptiveMetrics(includeDetailedBreakdown = false): Promise<{ metrics: AdaptiveMetrics; dashboardStats: DashboardStats; message: string }> {
    try {
      const cacheKey = `metrics.${await this.currentUid()}.${includeDetailedBreakdown}`;
      const cached = this.dashboardCacheGet<{ metrics: AdaptiveMetrics; dashboardStats: DashboardStats; message: string }>(cacheKey);
      if (cached) {
        return cached;
      }
      const result = await callWithTimeout(() => this.getAdaptiveMetricsFn({ includeDetailedBreakdown }));
      this.dashboardCacheSet(cacheKey, result.data);
      return result.data;
    } catch (error) {
      logger.error('QuizApiService', 'Failed to get adaptive metrics', { error });
      throw error;
    }
  }

  /**
   * Get personalized study plan
   */
  async getStudyPlan(): Promise<{ studyPlan: StudyPlan | null; message: string }> {
    try {
      const cacheKey = `studyPlan.${await this.currentUid()}`;
      const cached = this.dashboardCacheGet<{ studyPlan: StudyPlan | null; message: string }>(cacheKey);
      if (cached) {
        return cached;
      }
      const result = await callWithTimeout(() => this.getStudyPlanFn({}));
      this.dashboardCacheSet(cacheKey, result.data);
      return result.data;
    } catch (error) {
      logger.error('QuizApiService', 'Failed to get study plan', { error });
      throw error;
    }
  }

  /**
   * Study activity + streaks, computed server-side (single source of truth for
   * the dashboard heat map). Replaces the old browser-side recompute over
   * completed testResults, which missed practice days.
   */
  async getStudyActivity(): Promise<StudyActivityResult> {
    try {
      const cacheKey = `studyActivity.${await this.currentUid()}`;
      const cached = this.dashboardCacheGet<StudyActivityResult>(cacheKey);
      if (cached) {
        return cached;
      }
      const result = await callWithTimeout(() => this.getStudyActivityFn({}));
      this.dashboardCacheSet(cacheKey, result.data);
      return result.data;
    } catch (error) {
      logger.error('QuizApiService', 'Failed to get study activity', { error });
      throw error;
    }
  }

  /**
   * Get completed quiz results
   */
  async getQuizResults(sessionId: string): Promise<QuizResultsResponse> {
    try {
      logger.debug('QuizApiService', 'Getting quiz results', { sessionId });
      const result = await this.getQuizResultsFn({ sessionId });
      logger.debug('QuizApiService', 'Quiz results retrieved successfully', { sessionId });
      return result.data;
    } catch (error) {
      this.handleBackendError(error, 'Get quiz results');
    }
  }

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(data: CheckoutSessionRequest): Promise<CheckoutSessionResponse> {
    try {
      // Attach marketing attribution so the backend can tie this subscription's
      // future Stripe revenue back to the campaign that drove it.
      let attribution: ReturnType<typeof getCheckoutAttribution>;
      try { attribution = getCheckoutAttribution(); } catch (_e) { attribution = undefined; }
      const payload = attribution
        ? { ...data, attribution: attribution as unknown as Record<string, string> }
        : data;
      const result = await this.createCheckoutSessionFn(payload);
      return result.data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create checkout session';
      logger.error('QuizApiService', 'Error creating checkout session', { error: message });
      throw new Error(message);
    }
  }

  /**
   * Create Stripe customer portal session
   */
  async createPortalSession(returnUrl: string): Promise<{ url: string }> {
    try {
      const result = await this.createStripePortalSessionFn({ returnUrl });
      return result.data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create portal session';
      logger.error('QuizApiService', 'Error creating portal session', { error: message });
      throw new Error(message);
    }
  }

  /**
   * Get user's active quiz sessions
   * This will query both new backend sessions and legacy Firestore quizzes
   */
  async getUserActiveSessions(): Promise<{
    sessionId: string;
    testType: string;
    status: string;
    currentQuestionIndex: number;
    totalQuestions: number | null;
    score: number;
    lastActivityAt: string;
    createdAt: string;
  }[]> {
    try {
      logger.debug('QuizApiService', 'Getting user active sessions');
      
      // Check if function exists before calling
      if (!this.getUserActiveSessionsFn) {
        logger.error('QuizApiService', 'getUserActiveSessions function not initialized');
        return [];
      }
      
      const result = await callWithTimeout(() => this.getUserActiveSessionsFn(), { timeoutMs: 8000, retries: 0 });
      
      if (!result || !result.data) {
        logger.error('QuizApiService', 'Invalid response from getUserActiveSessions', { result });
        return [];
      }
      
      const sessions = result.data.sessions || [];
      
      logger.debug('QuizApiService', 'Active sessions retrieved', {
        count: sessions.length
      });

      // Normalize backend field names: getUserActiveSessions returns
      // questionsAnswered/accuracy/startedAt, but consumers (and this
      // method's declared type) use currentQuestionIndex/score/createdAt.
      // The mismatch rendered "Question NaN of 30" on the dashboard card.
      return (sessions as unknown as Array<Record<string, unknown>>).map((s) => ({
        sessionId: String(s.sessionId ?? ''),
        testType: String(s.testType ?? ''),
        status: String(s.status ?? ''),
        currentQuestionIndex: Number(s.questionsAnswered ?? s.currentQuestionIndex ?? 0),
        totalQuestions: Number(s.totalQuestions ?? 0),
        score: Number(s.score ?? s.accuracy ?? 0),
        lastActivityAt: String(s.lastActivityAt ?? ''),
        createdAt: String(s.createdAt ?? s.startedAt ?? s.lastActivityAt ?? '')
      }));
    } catch (error) {
      logger.error('QuizApiService', 'Failed to get active sessions', {
        error: error instanceof Error ? error.message : 'Unknown error',
        functionExists: !!this.getUserActiveSessionsFn
      });
      
      // Return empty array instead of throwing to prevent UI breaking
      return [];
    }
  }

  /**
   * Delete a quiz session
   * Handles both new backend sessions and legacy Firestore quizzes
   */
  async deleteQuizSession(sessionId: string): Promise<{ success: boolean }> {
    try {
      logger.debug('QuizApiService', 'Deleting quiz session', { sessionId });
      
      const result = await this.deleteQuizSessionFn({ sessionId });
      
      logger.debug('QuizApiService', 'Session deleted successfully', { sessionId });
      
      return result.data;
    } catch (error) {
      logger.error('QuizApiService', 'Failed to delete session', { error, sessionId });
      throw error;
    }
  }

  /**
   * Save test result and update user statistics
   * This method bridges the gap between quiz completion and dashboard metrics
   */
  async getUserTestResults(limit: number = 20, offset: number = 0): Promise<{ testResults: UserTestResult[]; summary: TestResultsSummary }> {
    try {
      logger.debug('QuizApiService', 'Getting user test results', { limit, offset });

      const response = await this.getUserTestResultsFn({
        limit,
        offset
      });

      logger.info('QuizApiService', 'Retrieved user test results', {
        count: response.data.testResults.length
      });

      return {
        testResults: response.data.testResults.map(convertTestResultToUserFormat),
        summary: response.data.summary
      };
    } catch (error) {
      logger.error('QuizApiService', 'Failed to get user test results', { error });
      throw error;
    }
  }

  async saveTestResult(testResult: TestResult, adaptiveState: SimpleAdaptiveState | AdaptiveState | StorageAdaptiveState): Promise<void> {
    try {
      logger.debug('QuizApiService', 'Saving test result', { 
        testResultId: testResult.id,
        score: testResult.score 
      });

      // Call backend saveTestResult function
      const response = await this.saveTestResultFn({
        testResult,
        adaptiveState
      });

      logger.info('QuizApiService', 'Test result saved successfully', { 
        testResultId: response.data.testResultId,
        message: response.data.message
      });

      logger.debug('QuizApiService', 'Test result save completed', { 
        testResultId: testResult.id 
      });
    } catch (error) {
      logger.error('QuizApiService', 'Failed to save test result', { error });
      throw error;
    }
  }
}

// Export singleton instance
export const quizApiService = QuizApiService.getInstance();

/** One day in the study heat map — already shaped for the calendar to render. */
export interface StudyActivityDay {
  date: string; // YYYY-MM-DD
  questionsAnswered: number;
  accuracy: number; // 0-100
  timeSpent: number; // minutes
  completed: boolean;
}

/** Response of the `getStudyActivity` callable (server-computed streaks + heat map). */
export interface StudyActivityResult {
  currentStreak: number;
  longestStreak: number;
  activeDays: number;
  totalQuestions: number;
  series: StudyActivityDay[];
  timezone: string;
  generatedAt: string;
}
interface TestResultsSummary {
  [key: string]: unknown;
}
